import { CaseChatButton } from "@/components/CaseChat/CaseChatButton";
import { CaseChatPanel } from "@/components/CaseChat/CaseChatPanel";
import { useMedicalChronology } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import { Loading } from "@/components/ui/Loading";
import { FeatureFlag } from "@/constants/featureFlags";
import { clearStoredConversation } from "@/hooks/useCaseChat";
import { useCaseChatPanel } from "@/hooks/useCaseChatPanel";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useChronologyCompareVersionsStore } from "@/store/chronologyCompareVersionsStore";
import React from "react";

import FileSidePanel from "../FileSidePanel";
import { Tabs, TabsContent } from "../ui/tabs";
import { Chats } from "./Chats";
import {
  COMPARE_VERSIONS_SECTIONS,
  CompareVersionPickerModal,
  VersionsCompare,
} from "./CompareVersions";
import { Files } from "./Files";
import { Images } from "./Images";
import { MedicalChronologyHeader } from "./MedicalChronologyHeader";
import { SkeletonView as PageSkeleton } from "./SkeletonView";
import { SummaryTab } from "./SummaryTab";
import { VersionNameModal } from "./VersionNameModal";
import { ViewTabs } from "./ViewTabs";
import { Visits } from "./Visits";
import { MED_CHRON_VIEW_TABS } from "./constants";
import type { MedChronViewTab } from "./types";

export const MedicalChronologyShell: React.FC = () => {
  const {
    caseId,
    isPageLoading,
    activeView,
    setActiveView,
    selectedVersionId,
    citationPanel,
    closeCitationPanel,
    setCitationPage,
    setCitationPanelWidth,
    isFullscreen,
    toggleFullscreen,
    versionNameModal,
    setVersionNameModalValue,
    saveVersionName,
    closeVersionNameModal,
    versions,
    isCreatingVersion,
  } = useMedicalChronology();
  const isComparing = useChronologyCompareVersionsStore((s) => Boolean(s.selectedBaseVersionId));
  const isComparePickerOpen = useChronologyCompareVersionsStore((s) => s.isComparePickerOpen);
  const closeComparePicker = useChronologyCompareVersionsStore((s) => s.closeComparePicker);
  const startCompare = useChronologyCompareVersionsStore((s) => s.startCompare);
  const resetCompareVersions = useChronologyCompareVersionsStore((s) => s.reset);

  React.useEffect(() => () => resetCompareVersions(), [resetCompareVersions]);

  const caseChatEnabled = useFeatureFlag(FeatureFlag.CaseChatEnabled);
  const {
    chatPanel,
    openChatPanel,
    openConversation,
    closeChatPanel,
    setChatPanelWidth,
    startNewChat,
  } = useCaseChatPanel();

  // Both panels dock to the right and can be open together: the file panel
  // shifts left by the chat panel's width so it never overlaps it. Reading
  // the CSS custom property (rather than the width state directly) keeps
  // this in sync during an active resize drag, not just after it settles.
  const citationRightOffset = chatPanel.isOpen ? "var(--chat-panel-width, 420px)" : "0px";

  // The file side panel is position:fixed; anchor its top to the bottom of the
  // case-name header so it opens *below* that bar (keeping the bar full-width)
  // rather than overlaying it. Exposed to CSS as --medchron-panel-top and passed
  // to FileSidePanel for its pixel height.
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const [panelTop, setPanelTop] = React.useState(64);

  React.useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => setPanelTop(Math.round(el.getBoundingClientRect().bottom));
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isPageLoading, citationPanel.isOpen]);

  if (isPageLoading) {
    return (
      <div className="medical-chronology-container bg-surface-subtle">
        <div className="flex h-full w-full flex-col px-4 py-2">
          <PageSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div
      className="medical-chronology-container bg-surface-subtle"
      style={{ "--medchron-panel-top": `${panelTop}px` } as React.CSSProperties}
    >
      <div className="flex h-full w-full flex-col px-4 py-2">
        <div ref={headerRef}>
          <MedicalChronologyHeader />
        </div>
        <div
          className={`medical-chronology min-h-0 flex-1 ${citationPanel.isOpen ? "with-right-panel" : ""} ${chatPanel.isOpen ? "with-chat-panel" : ""}`}
        >
          <Tabs
            value={activeView}
            onValueChange={(v) => setActiveView(v as MedChronViewTab)}
            className="w-full"
          >
            <ViewTabs caseChatEnabled={caseChatEnabled} />
            {/* Version-gated tabs: no fetch until a version is selected (shell-only guard). */}
            <TabsContent value={MED_CHRON_VIEW_TABS.SUMMARY} className="mt-0">
              {selectedVersionId ? (
                isComparing ? (
                  <VersionsCompare section={COMPARE_VERSIONS_SECTIONS.SUMMARY} />
                ) : (
                  <SummaryTab />
                )
              ) : null}
            </TabsContent>
            <TabsContent value={MED_CHRON_VIEW_TABS.VISITS} className="mt-0 space-y-2">
              {selectedVersionId ? (
                isComparing ? (
                  <VersionsCompare section={COMPARE_VERSIONS_SECTIONS.VISITS} />
                ) : (
                  <Visits />
                )
              ) : null}
            </TabsContent>
            <TabsContent value={MED_CHRON_VIEW_TABS.IMAGES} className="mt-0">
              <Images />
            </TabsContent>
            <TabsContent value={MED_CHRON_VIEW_TABS.FILES} className="mt-0">
              <Files />
            </TabsContent>
            {caseChatEnabled && (
              <TabsContent value={MED_CHRON_VIEW_TABS.CHATS} className="mt-0">
                <Chats
                  activeConversationId={chatPanel.isOpen ? chatPanel.activeConversationId : null}
                  onSelectConversation={openConversation}
                  onNewChat={() => {
                    clearStoredConversation(caseId);
                    startNewChat();
                    openChatPanel();
                  }}
                />
              </TabsContent>
            )}
          </Tabs>

          <VersionNameModal
            open={versionNameModal.open}
            value={versionNameModal.value}
            initialValue={versionNameModal.initialValue}
            onChange={setVersionNameModalValue}
            onSave={saveVersionName}
            onClose={closeVersionNameModal}
          />

          <CompareVersionPickerModal
            open={isComparePickerOpen}
            versions={versions}
            defaultBaseVersionId={selectedVersionId}
            onCompare={startCompare}
            onClose={closeComparePicker}
          />
        </div>
      </div>

      {isCreatingVersion && <Loading overlay text="Creating new version…" size={40} />}

      {citationPanel.isOpen && (
        <FileSidePanel
          isOpen={citationPanel.isOpen}
          onClose={closeCitationPanel}
          fileName={citationPanel.fileName}
          fileId={citationPanel.fileId ?? undefined}
          fileUrl={citationPanel.fileUrl}
          initialPage={citationPanel.pageNumber - 1}
          initialWidth={citationPanel.width}
          onPdfLoadError={citationPanel.onViewerLoadError}
          onPdfLoadSuccess={citationPanel.onViewerLoadSuccess}
          topOffset={panelTop}
          rightOffset={citationRightOffset}
          minWidth={300}
          maxWidth={800}
          onWidthChange={setCitationPanelWidth}
          onPageChange={setCitationPage}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
        />
      )}

      {caseChatEnabled && (
        // CaseChatPanel stays mounted across close/reopen so its conversation state
        // (via useCaseChat) survives — only `isOpen` toggles what it renders.
        <>
          <CaseChatPanel
            isOpen={chatPanel.isOpen}
            onClose={closeChatPanel}
            caseId={caseId}
            activeConversationId={chatPanel.activeConversationId}
            onNewConversation={startNewChat}
            resetToken={chatPanel.newChatToken}
            topOffset={panelTop}
            initialWidth={chatPanel.width}
            minWidth={340}
            maxWidth={640}
            onWidthChange={setChatPanelWidth}
          />
          {!chatPanel.isOpen && <CaseChatButton onClick={openChatPanel} />}
        </>
      )}
    </div>
  );
};
